import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { RouterStateSnapshot, TitleStrategy } from '@angular/router';

/** SEO data a route can attach via its ``data.seo`` to drive meta tags. */
export interface IRouteSeo {
  /** Rendered into ``<meta name="description">``. */
  description?: string;
  /** Rendered (comma-joined) into ``<meta name="keywords">``. */
  keywords?: string[];
}

/**
 * Canonical host for public, indexable pages. This MUST match the sitemap host in
 * ``generate-kb-catalog.mjs`` (its ``SITE_URL`` default) so the canonical URLs
 * and the sitemap entries point at the same place.
 */
const CANONICAL_ORIGIN = 'https://pulsewatch-app.facciobene.xyz';

/** Title for routes that do not set one, so a knowledge-base title never sticks around. */
const DEFAULT_TITLE = 'PulseWatch';

/**
 * Replaces Angular's default ``TitleStrategy`` so we keep the existing
 * route-``title`` → ``<title>`` behaviour AND, in addition, drive per-route SEO
 * meta tags (description, keywords, canonical link) from a route's ``data.seo``.
 *
 * Routes without ``data.seo`` get those tags removed again, so SEO copy from one
 * page never leaks into the next during client-side navigation.
 */
@Injectable({ providedIn: 'root' })
export class SeoTitleStrategy extends TitleStrategy {
  private readonly _title = inject(Title);
  private readonly _meta = inject(Meta);
  private readonly _doc = inject(DOCUMENT);

  override updateTitle(state: RouterStateSnapshot): void {
    this._title.setTitle(this.buildTitle(state) ?? DEFAULT_TITLE);

    // Walk to the deepest activated route — that's where leaf SEO data lives.
    let route = state.root;
    while (route.firstChild) {
      route = route.firstChild;
    }
    const seo = route.data?.['seo'] as IRouteSeo | undefined;

    if (seo?.description) {
      this._meta.updateTag({ name: 'description', content: seo.description });
    } else {
      this._meta.removeTag("name='description'");
    }

    if (seo?.keywords?.length) {
      this._meta.updateTag({ name: 'keywords', content: seo.keywords.join(', ') });
    } else {
      this._meta.removeTag("name='keywords'");
    }

    // Canonical only for pages that opt into SEO; strip the query/hash so every
    // variant of a URL collapses onto one canonical address.
    const canonical = seo ? `${CANONICAL_ORIGIN}${state.url.split(/[?#]/)[0]}` : null;
    this._setCanonical(canonical);
  }

  private _setCanonical(href: string | null): void {
    const head = this._doc.head;
    let link = head.querySelector<HTMLLinkElement>("link[rel='canonical']");
    if (!href) {
      if (link) {
        head.removeChild(link);
      }
      return;
    }
    if (!link) {
      link = this._doc.createElement('link');
      link.setAttribute('rel', 'canonical');
      head.appendChild(link);
    }
    link.setAttribute('href', href);
  }
}
